import { useQueryParamHelper } from '@shared/core/queryString';
import { useEventUserRole } from '@shared/components/AuthProvider';
import { useIsFeatureEnabled } from '@shared/core/featureFlags';
import { useCashFundEligibility } from './useCashFundEligibility';

type CashFundHotelSearch = Readonly<{
  /** All conditions satisfied → render the cash-fund variant. */
  isCashFundVariant: boolean;
  /** Gating decision still resolving (eligible cohort only) → route shows a skeleton. */
  isLoading: boolean;
}>;

// Escape hatch (mirrors checkout's `useCashFundCheckout`): `?forceCashFundCheckout=true` bypasses the couple + fund pre-check so the variant can be tested on deployed previews. Still requires the experiment flag = treatment, so it only surfaces the already-flag-gated feature.
const FORCE_CASH_FUND_PARAM = 'forceCashFundCheckout';

// Decides whether the guest hotel-search route renders the cash-fund variant; hooks run unconditionally and cohort scoping is via the flag's `skip`, never an `if`.
export const useCashFundHotelSearch = (): CashFundHotelSearch => {
  // `owner` and `admin` both qualify (both partners); exclude `superAdmin` (Joy staff) so they don't pollute the experiment.
  const { isLoggedIn, isAdmin, isSuperAdmin } = useEventUserRole();
  const isCouple = isLoggedIn && isAdmin && !isSuperAdmin;

  const hasFundBalance = useCashFundEligibility();

  const queryParamHelper = useQueryParamHelper();
  const forceEnabled = queryParamHelper.getValueString(FORCE_CASH_FUND_PARAM) === 'true';

  // Pre-experiment eligibility; when false we SKIP the flag read so the Amplitude exposure fires only for the eligible cohort.
  const eligibleBeforeExperiment = forceEnabled || (isCouple && hasFundBalance);

  const [isCashFundVariant, isFlagLoading] = useIsFeatureEnabled('hotelsSearchCashFundExperiment', {
    skip: !eligibleBeforeExperiment,
  });

  return {
    isCashFundVariant,
    // Existing guests mount the console immediately — zero added latency for the live flow. Only the non-prod
    // force escape hatch holds a skeleton (so testers land straight on cash-fund). A real treatment couple
    // briefly sees the guest console then swaps once the flag resolves — acceptable for the new cohort.
    isLoading: forceEnabled && isFlagLoading,
  };
};
